// UI / OrderTable.js
/* eslint-disable max-len */
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../hooks/useTranslation';
import {
  CCard, CCardBody, CTable, CTableHead, CTableRow, CTableHeaderCell, CTableBody, CTableDataCell,
  CPagination, CPaginationItem, CTooltip, CModal, CModalHeader, CModalTitle, CModalBody, CModalFooter,
  CButton, CRow, CCol, CFormInput, CCollapse, CBadge
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { cilPencil, cilTrash, cilInfo, cilMenu, cilFilter, cilX, cilSearch, cilChevronDoubleDown, cilChevronRight, cilArrowTop, cilArrowBottom, cilFactory } from '@coreui/icons';
import { mainColors } from '../../helpers/colors';
import { formatDateOnly } from '../../helpers/dateHelper';
import {
  deleteOrderRequest, getOrderRequest
} from '../../store/order/orderActions';
import { getCurrencyRequestAction } from '../../store/currency/currencyActions';
import { getOrderDropdown } from '../../services/endpoints';
import './OrderTable.scss';
import { useResizableColumns } from '../../hooks/useResizableColumns';
import ExportData from '../ExportData/ExportData';
import { formatScaledAmount } from '../../helpers/quantity';

function OrderTable(props) {
  // Redux state'ten server-side pagination bilgilerini al
  const { 
    orderList, 
    isLoading, 
    deleteOrderRequest,
    totalCount,
    totalPages: serverTotalPages
  } = props;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { getHeaderProps, getResizerFor } = useResizableColumns(
    ['expand', 'index', 'OrderNo', 'CustomerName', 'CustomerCode', 'WeaveCode', 'QuantityWithUnit', 'ProductSalesPrice', 'Name', 'OrderDatetime', 'OrderDeadlineDatetime', 'actions'],
    'OrderTable'
  );

  // Server-side Pagination state
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Server-side Sorting state
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('orderdatetime');

  const [deleteModal, setDeleteModal] = useState({ open: false, item: null });
  const [showFilters, setShowFilters] = useState(false);
  const [expandedRows, setExpandedRows] = useState(new Set());

  // Server-side filtreleme için state (backend'e gönderilecek)
  const [filters, setFilters] = useState({
    OrderNo: '',                    // LIKE (minimum 3 characters)
    CustomerName: '',               // LIKE
    CustomerProductCode: '',        // LIKE (CustomerCode alanı)
    ProductCode: '',                // LIKE (WeaveCode alanı)
    ProductQuantity: '',            // equality (integer - DB'de olduğu gibi)
    ProductSalesPrice: '',          // equality
    OrderStatusGuids: [],            // multiple exact match
    OrderStartDate: '',             // tarih (YYYY-MM-DD formatında UI'dan)
    OrderEndDate: '',               // tarih (YYYY-MM-DD formatında UI'dan)
    DeadlineStartDate: '',          // tarih (YYYY-MM-DD formatında UI'dan)
    DeadlineEndDate: '',            // tarih (YYYY-MM-DD formatında UI'dan)
    StockQuantityWhole: '',         // decimal whole part (OpsStocks tablosu)
    StockQuantityDecimal: '',       // decimal decimal part
    ShipmentQuantityWhole: '',      // decimal whole part (OpsShipments tablosu)
    ShipmentQuantityDecimal: ''     // decimal decimal part
  });

  // Decimal input helper: whole ve decimal kısımları integer'a çevir (wholePart * 100 + decimalPart)
  const toIntPrice = (whole, decimal) => {
    const wholeValue = String(whole || '').replace(/\D/g, '');
    const decimalValue = String(decimal || '').replace(/\D/g, '').slice(0, 2);

    if (!wholeValue && !decimalValue) {
      return null;
    }

    const wholePart = wholeValue ? parseInt(wholeValue, 10) : 0;
    const decimalPart = decimalValue ? parseInt(decimalValue.padEnd(2, '0'), 10) : 0;
    return (wholePart * 100) + decimalPart;
  };

  // Decimal input handler: whole part (tam sayı kısmı)
  const handleWholeInput = (e, field) => {
    const value = e.target.value.replace(/\D/g, '');
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  // Decimal input handler: decimal part (ondalık kısım, max 2 hane)
  const handleDecimalInput = (e, field) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 2);
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  // Decimal input: "." veya "," basınca decimal input'a geç
  const handlePriceKeyDown = (e, decimalField) => {
    const isDotOrComma = e.key === '.' || e.key === ',' ||
      e.keyCode === 190 || e.keyCode === 110 || e.keyCode === 188;
    if (isDotOrComma) {
      e.preventDefault();
      const decimalInput = document.querySelector(`input[name="${decimalField}"]`);
      if (decimalInput) {
        decimalInput.focus();
        decimalInput.select();
      }
    }
  };

  const [dropdownCurrencies, setDropdownCurrencies] = useState([]);
  const [orderStatusOptions, setOrderStatusOptions] = useState([]);
  const [showStatusFilter, setShowStatusFilter] = useState(false);

  // Tarih formatını YYYY-MM-DD'den DDMMYYYY'e çevir (backend formatı)
  const convertDateToBackendFormat = (dateStr) => {
    if (!dateStr) return null;
    // YYYY-MM-DD -> DDMMYYYY
    const parts = dateStr.split('-');
    if (parts.length !== 3) return null;
    const [year, month, day] = parts;
    return parseInt(`${day}${month}${year}`, 10);
  };

  // Server-side veri yükleme fonksiyonu
  const loadData = useCallback(() => {
    const trimmedOrderNo = filters.OrderNo.trim();
    const trimmedProductCode = filters.ProductCode.trim();
    const hasShortOrderNo = trimmedOrderNo.length > 0 && trimmedOrderNo.length < 3;
    const hasShortProductCode = trimmedProductCode.length > 0 && trimmedProductCode.length < 3;

    if (hasShortOrderNo || hasShortProductCode) {
      return;
    }

    const selectedOrderStatusGuids = Array.isArray(filters.OrderStatusGuids)
      ? filters.OrderStatusGuids.filter(Boolean)
      : [];

    // Backend'e gönderilecek payload'ı oluştur
    const payload = {
      // Filtre parametreleri (optional)
      OrderNo: trimmedOrderNo || null,
      CustomerName: filters.CustomerName || null,
      CustomerProductCode: filters.CustomerProductCode || null,
      ProductCode: trimmedProductCode || null,
      ProductQuantity: filters.ProductQuantity ? parseInt(filters.ProductQuantity, 10) : null,
      ProductSalesPrice: filters.ProductSalesPrice ? parseInt(filters.ProductSalesPrice, 10) : null,
      OrderStatusGuid: selectedOrderStatusGuids.length === 1 ? selectedOrderStatusGuids[0] : null,
      OrderStatusGuids: selectedOrderStatusGuids.length > 0 ? selectedOrderStatusGuids : null,
      // Tarih filtreleri (UI: YYYY-MM-DD -> Backend: DDMMYYYY)
      OrderStartDate: convertDateToBackendFormat(filters.OrderStartDate),
      OrderEndDate: convertDateToBackendFormat(filters.OrderEndDate),
      DeadlineStartDate: convertDateToBackendFormat(filters.DeadlineStartDate),
      DeadlineEndDate: convertDateToBackendFormat(filters.DeadlineEndDate),
      // Stok/Sevkiyat filtreleri (decimal input whole+decimal -> int * 100)
      StockQuantity: toIntPrice(filters.StockQuantityWhole, filters.StockQuantityDecimal),
      ShipmentQuantity: toIntPrice(filters.ShipmentQuantityWhole, filters.ShipmentQuantityDecimal),
      // Sıralama parametreleri
      OrderBy: orderBy,
      IsDescending: order === 'desc',
      // Sayfalama parametreleri
      PageNumber: page,
      PageSize: rowsPerPage
    };

    dispatch(getOrderRequest(payload));
  }, [dispatch, JSON.stringify(filters), orderBy, order, page, rowsPerPage]);

  // İlk yükleme ve dropdown'ları getir
  useEffect(() => {
    dispatch(getCurrencyRequestAction({}));
    (async () => {
      try {
        const userData = JSON.parse(localStorage.getItem('user') || '{}');
        const sessionUserId = localStorage.getItem('sessionUserId');
        const userId = sessionUserId || userData?.sessionUserId;
        const payload = {
          SessionUserId: userId,
          OrderId: '',
          IncludeOrders: false,
          IncludeProducts: false,
          IncludeWeaveColors: false,
        };
        const response = await getOrderDropdown(payload);
        const data = response?.data || {};
        const currenciesRaw = data.Currencies || [];
        const normalized = currenciesRaw.map(c => ({
          PrdCurrencyGuid: c.PrdCurrencyGuid || c.CostCurrencyGuid || c.SalesCurrencyGuid,
          CurrencyName: c.CurrencyName || c.CostCurrencyName || c.SalesCurrencyName,
          CurrencyCode: c.CurrencyCode || c.CostCurrencyCode || c.SalesCurrencyCode,
        })).filter(c => c.PrdCurrencyGuid);
        setDropdownCurrencies(normalized);
        setOrderStatusOptions(data.OrderStatuses || []);
      } catch (err) {
        setDropdownCurrencies([]);
        setOrderStatusOptions([]);
      }
    })();
  }, [dispatch]);

  // Filtre/pagination/sort değiştiğinde veri yükle
  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleEdit = (item) => {
    navigate(`/orders/edit/${item.OrderGuid}`, { state: { order: item } });
  };

  const handleDetail = (item) => {
    navigate(`/orders/detail/${item.OrderGuid}`, { state: { order: item } });
  };

  const handleAction = (item) => {
    navigate(`/orders/action/${item.OrderGuid}`, { state: { order: item } });
  };

  const handleSupplier = (item) => {
    navigate(`/orders/supplier/${item.OrderGuid}`, { state: { order: item } });
  };

  const handleDeleteClick = (item) => {
    setDeleteModal({ open: true, item });
  };

  const handleDeleteConfirm = () => {
    if (deleteModal.item) {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const sessionUserId = localStorage.getItem('sessionUserId');
      const userId = sessionUserId || userData?.sessionUserId;
      if (userId && userId !== 'undefined') {
        const deleteData = {
          SessionUserId: String(userId),
          OrderId: deleteModal.item.OrderGuid
        };
        deleteOrderRequest(deleteData);
      }
    }
    setDeleteModal({ open: false, item: null });
  };

  const handleDeleteCancel = () => {
    setDeleteModal({ open: false, item: null });
  };

  // Server-side pagination: sayfa değiştiğinde
  const handleChangePage = (newPage) => {
    setPage(newPage);
  };

  // Server-side pagination: sayfa boyutu değiştiğinde
  const handleChangeRowsPerPage = (value) => {
    setRowsPerPage(value);
    setPage(1);
  };

  // Server-side filtering: filtre değiştiğinde
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
    setPage(1);  // Filtre değişince ilk sayfaya dön
  };

  // Filtreleri temizle
  const clearFilters = () => {
    setFilters({
      OrderNo: '',
      CustomerName: '',
      CustomerProductCode: '',
      ProductCode: '',
      ProductQuantity: '',
      ProductSalesPrice: '',
      OrderStatusGuids: [],
      OrderStartDate: '',
      OrderEndDate: '',
      DeadlineStartDate: '',
      DeadlineEndDate: '',
      StockQuantityWhole: '',
      StockQuantityDecimal: '',
      ShipmentQuantityWhole: '',
      ShipmentQuantityDecimal: ''
    });
    setPage(1);
  };

  const toggleRowExpansion = (orderGuid) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(orderGuid)) {
      newExpandedRows.delete(orderGuid);
    } else {
      newExpandedRows.add(orderGuid);
    }
    setExpandedRows(newExpandedRows);
  };

  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  // Server-side sorting: sıralama değiştiğinde
  const handleRequestSort = (property) => {
    // UI column'u backend orderBy field'ına map'le
    const columnToOrderBy = {
      'OrderNo': 'orderno',
      'CustomerName': 'customername',
      'WeaveCode': 'productcode',
      'QuantityWithUnit': 'productquantity',
      'ProductSalesPrice': 'productsalesprice',
      'Name': 'orderstatus',
      'OrderDatetime': 'orderdatetime',
      'OrderDeadlineDatetime': 'deadlinedatetime'
    };

    const backendField = columnToOrderBy[property] || 'orderdatetime';
    const isAsc = orderBy === backendField && order === 'asc';
    const newOrder = isAsc ? 'desc' : 'asc';
    setOrder(newOrder);
    setOrderBy(backendField);
  };

  // Backend orderBy field'ından UI column'a çevir (sort indicator için)
  const getUiColumnFromOrderBy = (backendField) => {
    const orderByToColumn = {
      'orderno': 'OrderNo',
      'customername': 'CustomerName',
      'productcode': 'WeaveCode',
      'productquantity': 'QuantityWithUnit',
      'productsalesprice': 'ProductSalesPrice',
      'orderstatus': 'Name',
      'orderdatetime': 'OrderDatetime',
      'deadlinedatetime': 'OrderDeadlineDatetime'
    };
    return orderByToColumn[backendField] || null;
  };

  const currentSortColumn = getUiColumnFromOrderBy(orderBy);

  const selectedStatusGuids = Array.isArray(filters.OrderStatusGuids) ? filters.OrderStatusGuids : [];
  const selectedStatusNames = orderStatusOptions
    .filter((opt) => selectedStatusGuids.includes(String(opt.OpsOrderStatusGuid)))
    .map((opt) => opt.Name || opt.OpsOrderStatusGuid);
  const statusFilterLabel = selectedStatusNames.length > 0
    ? selectedStatusNames.join(', ')
    : (t('selectStatus') || 'Durum Seçin');

  const handleStatusToggle = (guid) => {
    const normalizedGuid = String(guid);
    setFilters((prev) => {
      const current = Array.isArray(prev.OrderStatusGuids) ? prev.OrderStatusGuids : [];
      const next = current.includes(normalizedGuid)
        ? current.filter((item) => item !== normalizedGuid)
        : [...current, normalizedGuid];
      return { ...prev, OrderStatusGuids: next };
    });
    setPage(1);
  };



  // Get currency symbol based on currency name
  const getCurrencySymbol = (currencyName) => {
    if (!currencyName) return '';

    const currency = currencyName.toLowerCase();

    if (currency.includes('euro') || currency.includes('eur')) return '€';
    if (currency.includes('dolar') || currency.includes('dollar') || currency.includes('usd')) return '$';
    if (currency.includes('türk') || currency.includes('turk') || currency.includes('try') || currency.includes('tl') || currency.includes('lira')) return '₺';
    if (currency.includes('pound') || currency.includes('sterlin') || currency.includes('gbp')) return '£';
    if (currency.includes('yen') || currency.includes('jpy')) return '¥';
    if (currency.includes('yuan') || currency.includes('cny')) return '¥';
    if (currency.includes('rupee') || currency.includes('inr')) return '₹';
    if (currency.includes('ruble') || currency.includes('rub')) return '₽';

    return currencyName;
  };

  // Resolve currency symbol by GUID using currency list (Redux + Dropdown fallback)
  const getCurrencySymbolByGuid = (guid) => {
    if (!guid) return '';
    const allCurrencies = [...(props.currencyList || []), ...dropdownCurrencies];
    const found = allCurrencies.find(c => c.PrdCurrencyGuid === guid);
    if (!found) return '';
    const code = (found.CurrencyCode || '').toLowerCase();
    const name = (found.CurrencyName || '').toLowerCase();
    if (code === 'eur' || code === 'euro') return '€';
    if (code === 'usd' || code === 'dollar') return '$';
    if (code === 'try' || code === 'tl' || code === 'turkish lira') return '₺';
    if (code === 'gbp' || code === 'pound') return '£';
    if (code === 'jpy' || code === 'yen') return '¥';
    if (code === 'cny' || code === 'yuan') return '¥';
    if (code === 'inr' || code === 'rupee') return '₹';
    if (code === 'rub' || code === 'ruble') return '₽';
    if (name.includes('euro') || name.includes('eur')) return '€';
    if (name.includes('dolar') || name.includes('dollar') || name.includes('usd')) return '$';
    if (name.includes('türk') || name.includes('turk') || name.includes('try') || name.includes('tl') || name.includes('lira')) return '₺';
    if (name.includes('pound') || name.includes('sterlin') || name.includes('gbp')) return '£';
    if (name.includes('yen') || name.includes('jpy')) return '¥';
    if (name.includes('yuan') || name.includes('cny')) return '¥';
    if (name.includes('rupee') || name.includes('inr')) return '₹';
    if (name.includes('ruble') || name.includes('rub')) return '₽';
    return found.CurrencyName || '';
  };

  const formatPrice = (price, guid, fallbackCurrencyName) => {
    if (!price || price === 0) return '-';
    const formattedPrice = (parseFloat(price) / 100).toFixed(2);
    let symbol = getCurrencySymbolByGuid(guid);
    if (!symbol && fallbackCurrencyName) {
      symbol = getCurrencySymbol(fallbackCurrencyName);
    }
    return symbol ? `${formattedPrice} ${symbol}` : formattedPrice;
  };

  // Server-side filtreleme - client-side filtreleme kaldırıldı
  // Tüm filtreleme backend'de yapılıyor, orderList zaten filtrelenmiş veriyi içeriyor

  // Server-side filtreleme için columns tanımı  
  // Tablo görünümünde gösterilecek sütunlar
  const columns = [
    { id: 'expand', label: '', sortable: false, filterable: false },
    { id: 'index', label: '#', sortable: false, filterable: false },
    { id: 'OrderNo', label: t('orderNo'), sortable: true, filterable: true, filterType: 'like' },
    { id: 'CustomerName', label: t('customerName'), sortable: true, filterable: true, filterType: 'like' },
    { id: 'CustomerCode', label: t('customerProductCode'), sortable: true, filterable: true, filterType: 'like' },
    { id: 'WeaveCode', label: t('productCode') || t('weaveCode'), sortable: true, filterable: true, filterType: 'like' },
    { id: 'QuantityWithUnit', label: t('productQuantity'), sortable: true, filterable: true, filterType: 'number' },
    { id: 'ProductSalesPrice', label: t('productSalesPrice'), sortable: true, filterable: true, filterType: 'number' },
    { id: 'Name', label: t('orderStatus'), sortable: true, filterable: true, filterType: 'status' },
    { id: 'OrderDatetime', label: t('orderDatetime'), sortable: true, filterable: true, filterType: 'dateRange' },
    { id: 'OrderDeadlineDatetime', label: t('orderDeadlineDatetime'), sortable: true, filterable: true, filterType: 'dateRange' },
    { id: 'actions', label: t('actions'), sortable: false, filterable: false }
  ];

  // Tüm sütunlar görünür (filterOnly kaldırıldı)
  const visibleColumns = columns;

  // Build export columns: include all non-GUID fields required by backend schema, plus visible ones (excluding control columns)
  const extraExportColumns = useMemo(() => {
    return [
      { id: 'OrderNo', label: t('orderNo') },
      { id: 'CustomerName', label: t('customerName') },
      { id: 'CustomerCode', label: t('customerCode') },
      { id: 'SupplierName', label: t('supplierName') },
      { id: 'UnitName', label: t('unitName') || 'Unit' },
      { id: 'CurrencyName', label: t('currencyName') || 'Currency' },
      { id: 'WeaveCode', label: t('productCode') || t('weaveCode') },
      { id: 'ProductQuantity', label: t('productQuantity') },
      { id: 'QuantityWithUnit', label: t('productQuantity') },
      { id: 'ProductCostPrice', label: t('productCostPrice') || 'Cost Price' },
      { id: 'ProductSalesPrice', label: t('productSalesPrice') || 'Sales Price' },
      { id: 'ProductTotalPrice', label: t('productTotalPrice') || 'Total Price' },
      { id: 'OrderDatetime', label: t('orderDatetime') },
      { id: 'OrderDeadlineDatetime', label: t('orderDeadlineDatetime') },
      { id: 'ProductionStartDatetime', label: t('productionStartDatetime') || 'Production Start' },
      { id: 'ProductionEndDatetime', label: t('productionEndDatetime') || 'Production End' },
      { id: 'InsertDatetime', label: t('insertDatetime') },
      { id: 'Description', label: t('description') },
      { id: 'Name', label: t('orderStatus') },
      { id: 'TotalStockQuantity', label: t('stock') },
      { id: 'TotalShippedQuantity', label: t('shipment') },
      { id: 'Balance', label: t('balance') },
      { id: 'TotalThreads', label: t('totalThreads') || 'Total Threads' },
      { id: 'ReedWidth', label: t('reedWidth') || 'Reed Width' },
      { id: 'ReedNo', label: t('reedNo') || 'Reed No' },
      { id: 'DGTS', label: t('dgts') || 'DGTS' },
      { id: 'WarpDensity', label: t('warpDensity') || 'Warp Density' },
      { id: 'WeftDensity', label: t('weftDensity') || 'Weft Density' },
      { id: 'PrdWeaveType', label: t('weaveType') || 'Weave Type' },
      // Warp/Weft details (from API)
      { id: 'WarpThreadCodes', label: `${t('warp')} ${t('yarnCode') || 'Yarn Code'}` },
      { id: 'WarpThreadNames', label: `${t('warp')} ${t('yarnName') || 'Yarn Name'}` },
      { id: 'WeftThreadCodes', label: `${t('weft')} ${t('yarnCode') || 'Yarn Code'}` },
      { id: 'WeftThreadNames', label: `${t('weft')} ${t('yarnName') || 'Yarn Name'}` },
    ];
  }, [t]);

  const exportColumns = useMemo(() => {
    const base = visibleColumns
      .filter(col => !['actions', 'expand', 'index'].includes(col.id))
      .filter(col => col.id === 'OrderNo' || !/guid/i.test(col.id));
    const extras = extraExportColumns.filter(ec => ec.id === 'OrderNo' || !/guid/i.test(ec.id));
    const byId = new Map();
    [...base, ...extras].forEach(c => { if (!byId.has(c.id)) byId.set(c.id, c); });
    return Array.from(byId.values());
  }, [visibleColumns, extraExportColumns]);

  // Server-side pagination - veri backend'den zaten sıralanmış ve sayfalanmış geliyor
  const getSortedData = () => {
    // Backend'den gelen veri zaten doğru sırada ve sayfada
    return orderList;
  };

  // Server-side pagination bilgileri Redux'tan geliyor
  const totalRows = totalCount || 0;
  const totalPages = serverTotalPages || Math.ceil(totalRows / rowsPerPage) || 1;
  const paginationItems = [];
  const maxVisiblePages = 7;
  let startPage = Math.max(1, page - Math.floor(maxVisiblePages / 2));
  let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
  if (endPage - startPage + 1 < maxVisiblePages) {
    startPage = Math.max(1, endPage - maxVisiblePages + 1);
  }

  if (startPage > 1) {
    paginationItems.push(
      <CPaginationItem key="first" onClick={() => handleChangePage(1)}>
        1
      </CPaginationItem>
    );
    if (startPage > 2) {
      paginationItems.push(
        <CPaginationItem key="start-ellipsis" disabled>
          ...
        </CPaginationItem>
      );
    }
  }

  for (let i = startPage; i <= endPage; i++) {
    paginationItems.push(
      <CPaginationItem key={i} active={i === page} onClick={() => handleChangePage(i)}>
        {i}
      </CPaginationItem>
    );
  }

  if (endPage < totalPages) {
    if (endPage < totalPages - 1) {
      paginationItems.push(
        <CPaginationItem key="end-ellipsis" disabled>
          ...
        </CPaginationItem>
      );
    }
    paginationItems.push(
      <CPaginationItem key="last" onClick={() => handleChangePage(totalPages)}>
        {totalPages}
      </CPaginationItem>
    );
  }

  const activeFilterCount = Object.values(filters).filter(value => Array.isArray(value) ? value.length > 0 : String(value || '').trim() !== '').length;

  const buildCellValue = (colId, item) => {
    switch (colId) {
      case 'QuantityWithUnit':
        return `${item.ProductQuantity ?? ''} ${item.UnitName ?? ''}`.trim() || '-';
      case 'ProductSalesPrice':
        return `${formatPrice(item.ProductSalesPrice, item.SalesCurrencyGuid, item.CurrencyName)}`;
      case 'ProductCostPrice':
        return `${formatPrice(item.ProductCostPrice, item.CostCurrencyGuid, item.CurrencyName)}`;
      case 'ProductTotalPrice':
        return `${formatPrice(item.ProductTotalPrice, item.SalesCurrencyGuid, item.CurrencyName)}`;
      case 'OrderDatetime':
      case 'OrderDeadlineDatetime':
        return formatDateOnly(item[colId]);
      case 'ProductionStartDatetime':
      case 'ProductionEndDatetime':
      case 'InsertDatetime':
        return formatDateOnly(item[colId]);
      case 'WeaveCode':
        // Display WeaveCode if exists, otherwise show YarnCode - YarnName
        return item.WeaveCode || (item.YarnCode && item.YarnName ? `${item.YarnCode} - ${item.YarnName}` : '');
      case 'WarpThreadCodes': {
        const list = Array.isArray(item.WarpColors) ? item.WarpColors : [];
        const codes = list.map(c => c?.YarnCode).filter(Boolean);
        return codes.length ? codes.join(' + ') : '';
      }
      case 'WarpThreadNames': {
        const list = Array.isArray(item.WarpColors) ? item.WarpColors : [];
        const names = list.map(c => c?.YarnName).filter(Boolean);
        return names.length ? names.join(' + ') : '';
      }
      case 'WeftThreadCodes': {
        const list = Array.isArray(item.WeftColors) ? item.WeftColors : [];
        const codes = list.map(c => c?.YarnCode).filter(Boolean);
        return codes.length ? codes.join(' + ') : '';
      }
      case 'WeftThreadNames': {
        const list = Array.isArray(item.WeftColors) ? item.WeftColors : [];
        const names = list.map(c => c?.YarnName).filter(Boolean);
        return names.length ? names.join(' + ') : '';
      }
      case 'OrderNo':
      case 'CustomerName':
      case 'CustomerCode':
      case 'SupplierName':
      case 'Name':
      case 'UnitName':
      case 'CurrencyName':
      case 'Description':
      case 'TotalStockQuantity':
      case 'TotalShippedQuantity':
      case 'Balance':
      case 'TotalThreads':
      case 'ReedWidth':
      case 'ReedNo':
      case 'DGTS':
      case 'WarpDensity':
      case 'WeftDensity':
      case 'PrdWeaveType':
      case 'ProductQuantity':
        return item[colId] ?? '';
      default:
        return item[colId] ?? '';
    }
  };

  return (
    <div className="order-table-container">
      <CCard>
        <CCardBody>
          <div className="filter-controls mb-3">
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center gap-2">
                <CButton
                  color={showFilters ? "primary" : "outline-primary"}
                  variant={showFilters ? "solid" : "outline"}
                  size="sm"
                  onClick={toggleFilters}
                  className="filter-toggle-btn"
                >
                  <CIcon icon={cilFilter} size="sm" className="me-1" />
                  {t('filters')}
                  {activeFilterCount > 0 && (
                    <span className="filter-count-badge ms-1">{activeFilterCount}</span>
                  )}
                </CButton>
                {activeFilterCount > 0 && (
                  <CButton
                    color="outline-secondary"
                    variant="outline"
                    size="sm"
                    onClick={clearFilters}
                    className="clear-filters-btn"
                  >
                    <CIcon icon={cilX} size="sm" className="me-1" />
                    {t('clearFilters')}
                  </CButton>
                )}

              </div>
              <div className="results-info d-flex align-items-center gap-2">
                <span className="text-muted">
                  {totalRows} {t('resultsFound')}
                </span>
                <ExportData
                  columns={exportColumns}
                  rows={getSortedData()}
                  allRows={orderList}
                  buildCellValue={buildCellValue}
                  filenamePrefix="orders_export"
                  triggerDisabled={!orderList || orderList.length === 0}
                />
              </div>
            </div>
          </div>

          {/* Server-side Filtre Alanları */}
          <CCollapse visible={showFilters}>
            <div className="filter-panel mb-4">
              <CRow className="g-3">
                {/* Sipariş No - minimum 3 karakter ile parçalı eşleşme */}
                <CCol xs={12} sm={6} md={4} lg={3}>
                  <div className="filter-field">
                    <label className="filter-label">{t('orderNo')}</label>
                    <div className="filter-input-wrapper">
                      <CIcon icon={cilSearch} className="filter-input-icon" />
                      <CFormInput
                        type="text"
                        placeholder={`${t('search')} ${t('orderNo').toLowerCase()}...`}
                        value={filters.OrderNo}
                        onChange={(e) => handleFilterChange('OrderNo', e.target.value)}
                        className="filter-input"
                        size="sm"
                      />
                      {filters.OrderNo && (
                        <CButton color="link" size="sm" className="filter-clear-btn" onClick={() => handleFilterChange('OrderNo', '')}>
                          <CIcon icon={cilX} size="sm" />
                        </CButton>
                      )}
                    </div>
                  </div>
                </CCol>

                {/* Müşteri Adı - like */}
                <CCol xs={12} sm={6} md={4} lg={3}>
                  <div className="filter-field">
                    <label className="filter-label">{t('customerName')}</label>
                    <div className="filter-input-wrapper">
                      <CIcon icon={cilSearch} className="filter-input-icon" />
                      <CFormInput
                        type="text"
                        placeholder={`${t('search')} ${t('customerName').toLowerCase()}...`}
                        value={filters.CustomerName}
                        onChange={(e) => handleFilterChange('CustomerName', e.target.value)}
                        className="filter-input"
                        size="sm"
                      />
                      {filters.CustomerName && (
                        <CButton color="link" size="sm" className="filter-clear-btn" onClick={() => handleFilterChange('CustomerName', '')}>
                          <CIcon icon={cilX} size="sm" />
                        </CButton>
                      )}
                    </div>
                  </div>
                </CCol>

                {/* Müşteri Ürün Kodu - like */}
                <CCol xs={12} sm={6} md={4} lg={3}>
                  <div className="filter-field">
                    <label className="filter-label">{t('customerProductCode')}</label>
                    <div className="filter-input-wrapper">
                      <CIcon icon={cilSearch} className="filter-input-icon" />
                      <CFormInput
                        type="text"
                        placeholder={`${t('search')} ${t('customerProductCode').toLowerCase()}...`}
                        value={filters.CustomerProductCode}
                        onChange={(e) => handleFilterChange('CustomerProductCode', e.target.value)}
                        className="filter-input"
                        size="sm"
                      />
                      {filters.CustomerProductCode && (
                        <CButton color="link" size="sm" className="filter-clear-btn" onClick={() => handleFilterChange('CustomerProductCode', '')}>
                          <CIcon icon={cilX} size="sm" />
                        </CButton>
                      )}
                    </div>
                  </div>
                </CCol>

                {/* Ürün Kodu - like */}
                <CCol xs={12} sm={6} md={4} lg={3}>
                  <div className="filter-field">
                    <label className="filter-label">{t('productCode') || t('weaveCode')}</label>
                    <div className="filter-input-wrapper">
                      <CIcon icon={cilSearch} className="filter-input-icon" />
                      <CFormInput
                        type="text"
                        placeholder={`${t('search')} ${(t('productCode') || t('weaveCode')).toLowerCase()}...`}
                        value={filters.ProductCode}
                        onChange={(e) => handleFilterChange('ProductCode', e.target.value)}
                        className="filter-input"
                        size="sm"
                      />
                      {filters.ProductCode && (
                        <CButton color="link" size="sm" className="filter-clear-btn" onClick={() => handleFilterChange('ProductCode', '')}>
                          <CIcon icon={cilX} size="sm" />
                        </CButton>
                      )}
                    </div>
                  </div>
                </CCol>

                {/* Ürün Miktarı - number input */}
                <CCol xs={12} sm={6} md={4} lg={3}>
                  <div className="filter-field">
                    <label className="filter-label">{t('productQuantity') || 'Ürün Miktarı'}</label>
                    <div className="filter-input-wrapper">
                      <CIcon icon={cilSearch} className="filter-input-icon" />
                      <CFormInput
                        type="number"
                        placeholder={`${t('search')} ${(t('productQuantity') || 'miktar').toLowerCase()}...`}
                        value={filters.ProductQuantity}
                        onChange={(e) => handleFilterChange('ProductQuantity', e.target.value)}
                        className="filter-input"
                        size="sm"
                      />
                      {filters.ProductQuantity && (
                        <CButton color="link" size="sm" className="filter-clear-btn" onClick={() => handleFilterChange('ProductQuantity', '')}>
                          <CIcon icon={cilX} size="sm" />
                        </CButton>
                      )}
                    </div>
                  </div>
                </CCol>

                {/* Sipariş Durumu - çoklu seçim dropdown */}
                <CCol xs={12} sm={6} md={4} lg={3}>
                  <div className="filter-field">
                    <label className="filter-label">{t('orderStatus')}</label>
                    <div className="ops-status-selector">
                      <button
                        type="button"
                        className={`ops-status-toggle ${selectedStatusGuids.length > 0 ? 'ops-has-selection' : ''}`}
                        onClick={() => setShowStatusFilter((prev) => !prev)}
                      >
                        <span>{statusFilterLabel}</span>
                        {selectedStatusGuids.length > 0 && (
                          <span className="ops-selection-count">{selectedStatusGuids.length}</span>
                        )}
                      </button>
                      {showStatusFilter && (
                        <div className="ops-status-menu">
                          {orderStatusOptions.map((opt) => {
                            const guid = String(opt.OpsOrderStatusGuid);
                            return (
                              <div key={guid} className="ops-status-option" onClick={() => handleStatusToggle(guid)}>
                                <div className="ops-status-checkbox">
                                  <input
                                    type="checkbox"
                                    checked={selectedStatusGuids.includes(guid)}
                                    onChange={() => handleStatusToggle(guid)}
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                  <label>{opt.Name || guid}</label>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                </CCol>

                {/* Sipariş Tarihi Aralığı */}
                <CCol xs={12} sm={6} md={4} lg={3}>
                  <div className="filter-field">
                    <label className="filter-label">{t('orderDatetime')} ({t('startDate') || 'Başlangıç'})</label>
                    <CFormInput
                      type="date"
                      value={filters.OrderStartDate}
                      onChange={(e) => handleFilterChange('OrderStartDate', e.target.value)}
                      className="filter-input"
                      size="sm"
                    />
                  </div>
                </CCol>
                <CCol xs={12} sm={6} md={4} lg={3}>
                  <div className="filter-field">
                    <label className="filter-label">{t('orderDatetime')} ({t('endDate') || 'Bitiş'})</label>
                    <CFormInput
                      type="date"
                      value={filters.OrderEndDate}
                      onChange={(e) => handleFilterChange('OrderEndDate', e.target.value)}
                      className="filter-input"
                      size="sm"
                    />
                  </div>
                </CCol>

                {/* Termin Tarihi Aralığı */}
                <CCol xs={12} sm={6} md={4} lg={3}>
                  <div className="filter-field">
                    <label className="filter-label">{t('orderDeadlineDatetime')} ({t('startDate') || 'Başlangıç'})</label>
                    <CFormInput
                      type="date"
                      value={filters.DeadlineStartDate}
                      onChange={(e) => handleFilterChange('DeadlineStartDate', e.target.value)}
                      className="filter-input"
                      size="sm"
                    />
                  </div>
                </CCol>
                <CCol xs={12} sm={6} md={4} lg={3}>
                  <div className="filter-field">
                    <label className="filter-label">{t('orderDeadlineDatetime')} ({t('endDate') || 'Bitiş'})</label>
                    <CFormInput
                      type="date"
                      value={filters.DeadlineEndDate}
                      onChange={(e) => handleFilterChange('DeadlineEndDate', e.target.value)}
                      className="filter-input"
                      size="sm"
                    />
                  </div>
                </CCol>

                {/* Stok Miktarı - decimal input */}
                <CCol xs={12} sm={6} md={4} lg={3}>
                  <div className="filter-field">
                    <label className="filter-label">{t('stock') || 'Stok'}</label>
                    <div className="decimal-input-group">
                      <CFormInput
                        type="text"
                        name="FilterStockQuantityWhole"
                        className="filter-input whole-input"
                        value={filters.StockQuantityWhole}
                        onChange={(e) => handleWholeInput(e, 'StockQuantityWhole')}
                        onKeyDown={(e) => handlePriceKeyDown(e, 'FilterStockQuantityDecimal')}
                        size="sm"
                        placeholder="0"
                      />
                      <span className="decimal-separator">,</span>
                      <CFormInput
                        type="text"
                        name="FilterStockQuantityDecimal"
                        className="filter-input decimal-input"
                        value={filters.StockQuantityDecimal}
                        onChange={(e) => handleDecimalInput(e, 'StockQuantityDecimal')}
                        size="sm"
                        placeholder="00"
                      />
                      {(filters.StockQuantityWhole || filters.StockQuantityDecimal) && (
                        <CButton color="link" size="sm" className="filter-clear-btn-decimal" onClick={() => setFilters(prev => ({ ...prev, StockQuantityWhole: '', StockQuantityDecimal: '' }))}>
                          <CIcon icon={cilX} size="sm" />
                        </CButton>
                      )}
                    </div>
                  </div>
                </CCol>

                {/* Sevkiyat Miktarı - decimal input */}
                <CCol xs={12} sm={6} md={4} lg={3}>
                  <div className="filter-field">
                    <label className="filter-label">{t('shipment') || 'Sevkiyat'}</label>
                    <div className="decimal-input-group">
                      <CFormInput
                        type="text"
                        name="FilterShipmentQuantityWhole"
                        className="filter-input whole-input"
                        value={filters.ShipmentQuantityWhole}
                        onChange={(e) => handleWholeInput(e, 'ShipmentQuantityWhole')}
                        onKeyDown={(e) => handlePriceKeyDown(e, 'FilterShipmentQuantityDecimal')}
                        size="sm"
                        placeholder="0"
                      />
                      <span className="decimal-separator">,</span>
                      <CFormInput
                        type="text"
                        name="FilterShipmentQuantityDecimal"
                        className="filter-input decimal-input"
                        value={filters.ShipmentQuantityDecimal}
                        onChange={(e) => handleDecimalInput(e, 'ShipmentQuantityDecimal')}
                        size="sm"
                        placeholder="00"
                      />
                      {(filters.ShipmentQuantityWhole || filters.ShipmentQuantityDecimal) && (
                        <CButton color="link" size="sm" className="filter-clear-btn-decimal" onClick={() => setFilters(prev => ({ ...prev, ShipmentQuantityWhole: '', ShipmentQuantityDecimal: '' }))}>
                          <CIcon icon={cilX} size="sm" />
                        </CButton>
                      )}
                    </div>
                  </div>
                </CCol>
              </CRow>
            </div>
          </CCollapse>

          {/* Tablo */}
          <div className="table-wrapper">
            <CTable hover className="order-table">
              <CTableHead>
                <CTableRow>
                  {visibleColumns.map((column) => {
                    const headerProps = getHeaderProps(column.id);
                    return (
                      <CTableHeaderCell
                        key={column.id}
                        className={`table-header-cell ${['ProductCostPrice', 'ProductSalesPrice'].includes(column.id) ? 'price-column-header' : ''}`}
                        style={{ cursor: column.sortable ? 'pointer' : 'default', paddingRight: '8px', ...(headerProps.style || {}) }}
                        onClick={() => column.sortable && handleRequestSort(column.id)}
                      >
                        {column.label}
                        {column.sortable && currentSortColumn === column.id && (
                          <CIcon icon={order === 'asc' ? cilArrowTop : cilArrowBottom} size="sm" className="ms-1" />
                        )}
                        {getResizerFor(column.id)}
                      </CTableHeaderCell>
                    );
                  })}
                </CTableRow>
              </CTableHead>
              <CTableBody>
                {getSortedData().length > 0 ? (
                  getSortedData().map((item, index) => {
                    const isExpanded = expandedRows.has(item.OrderGuid);
                    return (
                      <React.Fragment key={item.OrderGuid || index}>
                        <CTableRow className={isExpanded ? 'expanded-row' : ''}>
                          <CTableDataCell className="expand-cell">
                            <div
                              className="expand-button"
                              onClick={() => toggleRowExpansion(item.OrderGuid)}
                            >
                              <CIcon
                                icon={isExpanded ? cilChevronDoubleDown : cilChevronRight}
                                size="sm"
                                className="expand-icon"
                              />
                            </div>
                          </CTableDataCell>
                          <CTableDataCell>{(page - 1) * rowsPerPage + index + 1}</CTableDataCell>
                          <CTableDataCell>
                            <CBadge color="primary" shape="rounded-pill">
                              {item.OrderNo}
                            </CBadge>
                          </CTableDataCell>
                          {/* <CTableDataCell>{item.ProductCodeForCustomer}</CTableDataCell> */}
                          <CTableDataCell>{item.CustomerName}</CTableDataCell>
                          <CTableDataCell>{item.CustomerCode || '-'}</CTableDataCell>
                          <CTableDataCell>
                            {item.WeaveCode || (item.YarnCode && item.YarnName ? `${item.YarnCode} - ${item.YarnName}` : '-')}
                          </CTableDataCell>

                          <CTableDataCell>{`${item.ProductQuantity ?? ''} ${item.UnitName ?? ''}`.trim() || '-'}</CTableDataCell>
                          <CTableDataCell className="price-column price-sales">{formatPrice(item.ProductSalesPrice, item.SalesCurrencyGuid, item.CurrencyName)}</CTableDataCell>
                          <CTableDataCell>{item.Name}</CTableDataCell>
                          <CTableDataCell>{formatDateOnly(item.OrderDatetime)}</CTableDataCell>
                          <CTableDataCell>{formatDateOnly(item.OrderDeadlineDatetime)}</CTableDataCell>
                          <CTableDataCell>
                            <div className="d-flex gap-2">
                              <CTooltip content={t('viewDetails')}>
                                <div className="cursor-pointer p-1" onClick={() => handleDetail(item)}>
                                  <CIcon icon={cilInfo} size="sm" style={{ color: '#17a2b8' }} />
                                </div>
                              </CTooltip>
                              <CTooltip content={t('editOrder')}>
                                <div className="cursor-pointer p-1" onClick={() => handleEdit(item)}>
                                  <CIcon icon={cilPencil} size="sm" style={{ color: mainColors.primary }} />
                                </div>
                              </CTooltip>
                              <CTooltip content={t('orderAction')}>
                                <div className="cursor-pointer p-1" onClick={() => handleAction(item)}>
                                  <CIcon icon={cilMenu} size="sm" style={{ color: '#28a745' }} />
                                </div>
                              </CTooltip>
                              <CTooltip content={t('supplier') || 'Supplier'}>
                                <div className="cursor-pointer p-1" onClick={() => handleSupplier(item)}>
                                  <CIcon icon={cilFactory} size="sm" style={{ color: '#6f42c1' }} />
                                </div>
                              </CTooltip>
                              <CTooltip content={t('deleteOrder')}>
                                <div className="cursor-pointer p-1" onClick={() => handleDeleteClick(item)}>
                                  <CIcon icon={cilTrash} size="sm" style={{ color: '#dc3545' }} />
                                </div>
                              </CTooltip>
                            </div>
                          </CTableDataCell>
                        </CTableRow>

                        {isExpanded && (
                          <CTableRow className="detail-row">
                            <CTableDataCell colSpan={visibleColumns.length} className="detail-cell">
                              <div className="detail-content">
                                <div className="detail-grid">
                                  <div className="detail-item">
                                    <span className="detail-label">{t('stock') || 'Stok'}:</span>
                                    <span className="detail-value">{formatScaledAmount(item.TotalStockQuantity) || '-'}</span>
                                  </div>
                                  <div className="detail-item">
                                    <span className="detail-label">{t('shipment') || 'Sevkiyat'}:</span>
                                    <span className="detail-value">{formatScaledAmount(item.TotalShippedQuantity) || '-'}</span>
                                  </div>
                                  <div className="detail-item">
                                    <span className="detail-label">{t('balance') || 'Bakiye'}:</span>
                                    <span className="detail-value total-price-value">{formatScaledAmount(item.Balance) || '-'}</span>
                                  </div>
                                  {item.Note && (
                                    <div className="detail-item detail-item-full">
                                      <span className="detail-label">{t('note') || 'Not'}:</span>
                                      <span className="detail-value">
                                        {item.Note.length > 20 ? item.Note.substring(0, 20) + "..." : item.Note}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </CTableDataCell>
                          </CTableRow>
                        )}
                      </React.Fragment>
                    );
                  })
                ) : (
                  <CTableRow>
                    <CTableDataCell colSpan={visibleColumns.length} className="text-center py-4">
                      <div className="no-data-message">
                        {isLoading ? (
                          <div className="loading-message">
                            <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                            {t('loadingData')}
                          </div>
                        ) : activeFilterCount > 0 ? (
                          <div className="no-results-message">
                            <CIcon icon={cilSearch} size="xl" className="mb-2 text-muted" />
                            <div>{t('noResultsFound')}</div>
                            <CButton
                              color="link"
                              size="sm"
                              onClick={clearFilters}
                              className="mt-2"
                            >
                              {t('clearFiltersToSeeAll')}
                            </CButton>
                          </div>
                        ) : (
                          <div className="text-muted">{t('noDataFound')}</div>
                        )}
                      </div>
                    </CTableDataCell>
                  </CTableRow>
                )}
              </CTableBody>
            </CTable>
          </div>

          {totalRows > 0 && (
            <div className="d-flex justify-content-between align-items-center mt-3">
              <div className="d-flex align-items-center gap-2">
                <span className="text-muted">{t('show')}:</span>
                <select
                  className="form-select form-select-sm"
                  value={rowsPerPage}
                  onChange={(e) => handleChangeRowsPerPage(parseInt(e.target.value, 10))}
                  style={{ width: 'auto' }}
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
                <span className="text-muted">{t('entries')}</span>
              </div>
              <CPagination className="mb-0">
                <CPaginationItem
                  aria-label={t('previous')}
                  onClick={() => page > 1 && handleChangePage(page - 1)}
                  disabled={page === 1}
                >
                  &laquo;
                </CPaginationItem>
                {paginationItems}
                <CPaginationItem
                  aria-label={t('next')}
                  onClick={() => page < totalPages && handleChangePage(page + 1)}
                  disabled={page === totalPages}
                >
                  &raquo;
                </CPaginationItem>
              </CPagination>
              <div className="text-muted">
                {t('showing')} {Math.min((page - 1) * rowsPerPage + 1, totalRows)} {t('to')}{' '}
                {Math.min(page * rowsPerPage, totalRows)} {t('of')} {totalRows} {t('entries')}
              </div>
            </div>
          )}
        </CCardBody>
      </CCard>

      {/* Delete Modal */}
      <CModal visible={deleteModal.open} onClose={handleDeleteCancel}>
        <CModalHeader>
          <CModalTitle>{t('deleteOrder')}</CModalTitle>
        </CModalHeader>
        <CModalBody>
          <p>{t('confirmDeleteOrder')}</p>
          {deleteModal.item && (
            <div className="bg-light p-3 rounded">
              <strong>{t('orderNo')}:</strong> {deleteModal.item.OrderNo}<br />
              <strong>{t('customerName')}:</strong> {deleteModal.item.CustomerName}
            </div>
          )}
          <p className="text-danger mt-2 mb-0">
            <small>{t('orderDeleteWarning')}</small>
          </p>
        </CModalBody>
        <CModalFooter>
          <CButton color="secondary" onClick={handleDeleteCancel}>{t('cancel')}</CButton>
          <CButton color="danger" onClick={handleDeleteConfirm}>{t('delete')}</CButton>
        </CModalFooter>
      </CModal>


    </div>
  );
}

const mapStateToProps = (state) => ({
  orderList: state.orderReducer.orderList || [],
  isLoading: state.orderReducer.isLoading,
  currencyList: state.currencyReducer?.currencyList || [],
  // Server-side pagination bilgileri
  totalCount: state.orderReducer.totalCount || 0,
  totalPages: state.orderReducer.totalPages || 0,
  pageNumber: state.orderReducer.pageNumber || 1,
  pageSize: state.orderReducer.pageSize || 10,
  hasPreviousPage: state.orderReducer.hasPreviousPage || false,
  hasNextPage: state.orderReducer.hasNextPage || false
});

const mapDispatchToProps = (dispatch) => ({
  deleteOrderRequest: (payload) => dispatch(deleteOrderRequest(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(OrderTable);
